import React from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { useRouter } from 'next/compat/router';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';
import { KIOSK_PRO_MEDIA } from 'src/components/kiosk-pro/kiosk-pro-shared/kiosk-pro-shared.styles';

import useTranslation from 'hooks/use-translation';
import useProductQueryParams from 'hooks/use-product-query-params';
import useUI from 'hooks/use-ui';
import { useHideSalesLanguage } from 'src/hooks/use-hide-sales-language';

import SubcategoriesPills from 'components/subcategories-pills';
import ChevronIcon from 'shared/components/chevron-icon';
import { MobileOnly, DesktopOnly, Clickable } from 'shared/components';
import IndividualBogoCard from 'shared/components/specials/individual-bogo-card';
import { SpecialDetailHeader } from 'src/components/special-detail-header/special-detail-header';
import { Typography } from 'src/typography';
import { MenuSortV2 } from 'src/components/menu-sort-v2';
import SvgChevronIcon from 'src/assets/chevron-icon';
import { useSpecialsRedesignEnabled } from 'src/dispensary/specials/use-specials-redesign-enabled';

export default function ContentHeader(props) {
  const {
    children,
    heading,
    className,
    searchString,
    count,
    dispensary,
    isMobileEcommApp,
    selectedSpecial,
    offerSpecialPage,
    saleSpecialPage,
    showExpiredSpecial = false,
    customHeaderComponent,
    belowHeaderComponent,
    hideSort = false,
    subheader,
  } = props;

  const router = useRouter();
  const isSpecialsRedesignEnabled = useSpecialsRedesignEnabled();
  const hideBorder = true;

  const showSearchHeader = !!searchString;
  const specialPageVariant = offerSpecialPage || saleSpecialPage;

  const isBrands = router?.pathname?.includes('brands');
  const isKioskProEnabled = useKioskPro();
  const { isKiosk } = useUI();
  const isKioskProBrandsPage = isKioskProEnabled && isKiosk && isBrands;

  const hideBackNavigation = isSpecialsRedesignEnabled && specialPageVariant;
  // Only apply the mobile column layout when the heading actually renders (flag on); when the
  // flag is off the heading stays DesktopOnly, so this must not key on specialPageVariant alone.
  const specialPageColumnLayout = isSpecialsRedesignEnabled && specialPageVariant;

  function renderMenuHeaderTitle() {
    if (isKioskProBrandsPage) {
      return (
        <KioskProHeading size='medium' tag='h1' casing='capitalize' data-testid='menu-header-title'>
          <StyledBackButton onClick={() => router.back()}>
            <StyledSvgChevronIcon />
          </StyledBackButton>
          {heading}
        </KioskProHeading>
      );
    }
    if (hideBackNavigation) {
      return (
        <Typography.Heading size='medium' tag='h1' casing='capitalize' data-testid='menu-header-title'>
          {heading}
        </Typography.Heading>
      );
    }
    return (
      <ClickableBackHeader onClick={() => router?.back()}>
        <BackIconTitle />
        <Typography.Heading size='medium' tag='h1' casing='capitalize' data-testid='menu-header-title'>
          {heading}
        </Typography.Heading>
      </ClickableBackHeader>
    );
  }

  let menuHeaderTitle = <Text specialPageVariant={specialPageColumnLayout}>{renderMenuHeaderTitle()}</Text>;

  if (showSearchHeader) {
    menuHeaderTitle = <SearchResultsHeader searchString={searchString} count={count} />;
  }

  const shouldShowSpecialCard = specialPageVariant && selectedSpecial;
  // ECOMM-13289: when the category page resolves a valid SDK-configured
  // subheader, the H1 and subheader stack together and the sort/layout
  // controls move to their own right-aligned row beneath them (desktop
  // only -- see TitleColumn/Filters below). With no subheader, this is a
  // no-op and today's layout is completely unchanged.
  const hasSubheader = !!subheader;

  return (
    <OuterContainer hideBorder={hideBorder}>
      <HeadingContainer className={className} data-testid='heading-container'>
        {!isMobileEcommApp && specialPageVariant && <AllSpecialsBreadcrumb dispensary={dispensary} />}

        {(shouldShowSpecialCard || belowHeaderComponent) && (
          <FullWidthContainer>
            {shouldShowSpecialCard &&
              (isSpecialsRedesignEnabled ? (
                <SpecialDetailHeader
                  displayDescription={selectedSpecial.menuDisplayDescription || selectedSpecial.description}
                  showExpiredSpecial={showExpiredSpecial}
                />
              ) : (
                <OfferCardContainer>
                  <IndividualBogoCard
                    displayImage={selectedSpecial.menuDisplayImage}
                    displayName={selectedSpecial.menuDisplayName || selectedSpecial.name}
                    displayDescription={selectedSpecial.menuDisplayDescription || selectedSpecial.description}
                    showExpiredSpecial={showExpiredSpecial}
                  />
                </OfferCardContainer>
              ))}

            {belowHeaderComponent}
          </FullWidthContainer>
        )}

        <MobileOnly>
          {specialPageVariant && (
            <PillContainer>
              <SubcategoriesPills
                isSpecials
                specialId={selectedSpecial?._id}
                specialType={selectedSpecial?.specialType}
              />
            </PillContainer>
          )}
        </MobileOnly>
        {!showExpiredSpecial && (
          <MenuHeaderWithFilters specialPageVariant={specialPageColumnLayout} data-testid='menu-header-with-filters'>
            {customHeaderComponent}

            <TitleColumn hasSubheader={hasSubheader} data-testid='category-title-column'>
              {isSpecialsRedesignEnabled && specialPageVariant ? (
                !isMobileEcommApp && menuHeaderTitle
              ) : (
                <DesktopOnly>{!isMobileEcommApp && menuHeaderTitle}</DesktopOnly>
              )}
              {!isMobileEcommApp && isKioskProBrandsPage && <MobileOnly>{menuHeaderTitle}</MobileOnly>}

              {/* Mobile renders its own H1 + subheader block above <Menu> (see category.jsx), in the
                  approved prototype order (H1, subheader, category pills, controls), so this
                  desktop-title subheader must stay DesktopOnly to avoid rendering it twice on
                  mobile viewports. */}
              {hasSubheader && (
                <DesktopOnly>
                  <SubheaderContainer data-testid='category-subheader-container'>{subheader}</SubheaderContainer>
                </DesktopOnly>
              )}
            </TitleColumn>

            {!hideSort && !isKioskProBrandsPage && (
              <MobileOnly>
                <MenuSortV2 />
              </MobileOnly>
            )}

            {children && <Filters hasSubheader={hasSubheader}>{children}</Filters>}
          </MenuHeaderWithFilters>
        )}
      </HeadingContainer>
    </OuterContainer>
  );
}

const MenuHeaderWithFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;

  ${({ theme, specialPageVariant }) =>
    specialPageVariant &&
    `
    ${theme.breakpoints.down('md')} {
      flex-direction: column;
      align-items: flex-start;
    }
  `}
`;

// ECOMM-13289: the "stack title+subheader, wrap sort/layout controls onto their own
// full-width right-aligned row below" behavior is intentionally scoped to
// `theme.breakpoints.up('md')` (960px+) only. `MenuHeaderWithFilters` is a wrapping flex
// row with `justify-content: space-between`; giving `TitleColumn` (and `Filters`, below)
// `flex-basis: 100%` at md+ forces `Filters` onto its own wrapped line while preserving
// the row's native right-alignment -- matching today's desktop layout exactly. Below
// that breakpoint no override applies at all, so the existing mobile layout (rendered
// separately here -- see category.jsx for the mobile H1+subheader block) is untouched.
const TitleColumn = styled.div`
  /* Preserve the original mobile flex-child structure: this wrapper exists
     only to create the desktop title/subheader column and must not consume
     space beside the mobile sort/filter controls. */
  display: contents;

  ${({ theme, hasSubheader }) => `
    ${theme.breakpoints.up('md')} {
      display: flex;
      flex-direction: column;
      flex: 1 0 0%;
      min-width: 0;
      ${hasSubheader ? 'flex-basis: 100%;' : ''}
    }
  `}
`;

// No margin-top here -- CategorySubheader (via the shared HeadingDescription primitive)
// owns its own top spacing (matching CollectionsHeader's Description), so desktop
// doesn't stack two top margins.
const SubheaderContainer = styled.div`
  width: 100%;
`;

const AllSpecialsBreadcrumb = ({ dispensary }) => {
  const UI = useUI();
  const { t } = useTranslation();
  const hideSalesLanguage = useHideSalesLanguage();
  const pageTitle = hideSalesLanguage ? t(`common.all_offers`, `All Offers`) : t(`common.all_specials`, `All Specials`);

  return (
    <BreadcrumbContainer>
      <Link href={`/${UI.dispensaryRootUrl}/${dispensary.cName}/specials`}>
        <StyledSpan>
          <BackIcon fill='var(--dct--customized-colors-buttonsLinks)' />
          {pageTitle}
        </StyledSpan>
      </Link>
    </BreadcrumbContainer>
  );
};

const SearchResultsHeader = ({ count, searchString }) => {
  const { t } = useTranslation();
  const { resetQueryParams } = useProductQueryParams();

  return (
    <SearchResultsContainer data-testid='menu-header-title'>
      {t('common.showing', {
        defaultValue: 'Showing {{count}} result for:', // showing 1 result for
        defaultValue_0: 'Showing {{count}} results for:', // showing 0 results for
        defaultValue_plural: 'Showing {{count}} results for:', // showing 2+ results for
        count,
      })}{' '}
      <span id='search-string'>"{searchString}"</span>
      <Clickable id='clear' onClick={resetQueryParams}>
        {t('common.clear', 'clear')}
      </Clickable>
    </SearchResultsContainer>
  );
};

const BreadcrumbContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledSpan = styled.span`
  font-weight: bold;
  font-size: 14px;
  line-height: 175%;
  color: var(--dct--customized-colors-buttonsLinks);
  margin-bottom: 10px;

  &:hover {
    cursor: pointer;
  }
`;

const BackIcon = styled(ChevronIcon)`
  transform: rotate(90deg);
  margin-right: 5px;
  display: inline-block;
`;

const SearchResultsContainer = styled.div`
  text-transform: initial;
  font-size: 14px;

  #search-string {
    font-weight: bold;
  }
  #clear {
    color: var(--dct--customized-colors-buttonsLinks);
    display: inline;
    text-transform: lowercase;
    margin-left: 5px;
  }
`;

const HeadingContainer = styled.header`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const OuterContainer = styled.div`
  ${(props) =>
    !props.hideBorder &&
    `
  border-bottom: 1px solid var(--dcm-content-header-border-color, ${props.theme.colors.blueGrey[90]});
`}

  padding-bottom: 16px;
  ${({ theme }) => theme.breakpoints.down('md')} {
    padding-bottom: 15px;
  }
`;

const Text = styled.div`
  flex: 1 0 0%;
  min-width: 0;
  display: flex;
  align-items: center;
  color: var(--dcm-content-header-text-color, ${({ theme }) => theme.colors.primaryBlack});

  ${({ theme, specialPageVariant }) =>
    specialPageVariant &&
    `
    ${theme.breakpoints.down('md')} {
      flex: 1 1 100%;
      width: 100%;
    }
  `}
`;

const Filters = styled.div`
  flex: 0 0 auto;
  min-width: 0;
  padding-left: 12px;
  display: flex;
  align-items: center;

  ${({ theme, hasSubheader }) =>
    hasSubheader &&
    `
    ${theme.breakpoints.up('md')} {
      flex-basis: 100%;
      justify-content: flex-end;
      padding-left: 0;
      margin-top: 12px;
    }
  `}
`;

const PillContainer = styled.div`
  ${({ theme }) => theme.breakpoints.down(`md`)} {
    width: 100%;
    .subcategories-pills {
      margin: 11px 0 40px;
      div {
        &:nth-child(1) {
          padding-left: 0px;
        }
      }
    }
  }
`;

const FullWidthContainer = styled.div`
  width: 100%;
`;

const OfferCardContainer = styled.div`
  width: 100%;
  ${({ theme }) => theme.breakpoints.down(`md`)} {
    margin-bottom: 24px;
    margin-top: 6px;
  }
`;

const StyledBackButton = styled.button`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 800px;

  background: ${({ theme }) => theme.colors.grey[95]};
  border: none;
  cursor: pointer;
  box-shadow: 0px 4px 6px 0px rgba(0, 0, 0, 0.2);
`;

const StyledSvgChevronIcon = styled(SvgChevronIcon)`
  transform: rotate(90deg);
  color: ${({ theme }) => theme.colors.black};
  width: 16px;
`;

const KioskProHeading = styled(Typography.Heading)`
  font-size: 36px;
  display: flex;
  align-items: center;
  gap: 20px;

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth} {
    font-size: 28px;
  }

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    font-size: 28px;
  }
`;

const BackIconTitle = styled(BackIcon)`
  margin-right: 10px;
`;

const ClickableBackHeader = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`;
